import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { testPush, requestAndSubscribe, getSubscriptionStatus, forceResubscribe } from '../services/pushClient';
import BuyMeALatte from '../components/BuyMeALatte';

const WINDOW_LABELS = {
  '24h': '24 hours before',
  '2h':  '2 hours before',
  '30m': '30 min before',
};

// Email notification categories, grouped for the preference center.
// 'reminders' is intentionally NOT here — it has its own dedicated card
// below that writes to a separate endpoint (updateReminderPrefs).
const EMAIL_PREF_GROUPS = [
  {
    title: 'Your tee times',
    rows: [
      { key: 'playerJoined',     label: 'Someone joins a tee time I\u2019m hosting' },
      { key: 'playerCancelled',  label: 'Someone cancels their spot on my tee time' },
      { key: 'teeTimeEdited',    label: 'A tee time I\u2019m on is edited (date/time changed)' },
      { key: 'teeTimeCancelled', label: 'A tee time I\u2019m on is cancelled by the host' },
      { key: 'hostAddedYou',     label: 'A host adds me to a tee time' },
      { key: 'waitlistOpen',     label: 'A waitlist spot opens up for me' },
    ],
  },
  {
    title: 'Suggestions',
    rows: [
      { key: 'playerSuggested',  label: 'Someone suggests a player for my tee time' },
    ],
  },
  {
    title: 'Discovery',
    rows: [
      { key: 'newTeeTimePosted', label: 'A new tee time is posted to my groups or All Friends' },
    ],
  },
  {
    title: 'Conversations',
    rows: [
      { key: 'newComment',       label: 'New comments in my tee time threads' },
    ],
  },
];

export default function ProfilePage() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState(user?.phone || '');
  const [emailNotif, setEmailNotif] = useState(user?.email_notifications ?? true);
  const [emailPrefs, setEmailPrefs] = useState(user?.email_prefs || {
    playerJoined: true,
    playerCancelled: true,
    teeTimeEdited: true,
    teeTimeCancelled: true,
    hostAddedYou: true,
    waitlistOpen: true,
    playerSuggested: true,
    newTeeTimePosted: true,
    newComment: true,
    reminders: true,
  });
  const [handicap, setHandicap] = useState(user?.handicap ?? '');
  const [ghinNumber, setGhinNumber] = useState(user?.ghin_number || '');
  const [sharePhone, setSharePhone] = useState(user?.share_phone ?? false);
  const [shareEmail, setShareEmail] = useState(user?.share_email ?? false);
  // App-Wide discovery: typed region label (zip code or "City, ST") + radius.
  // The label is what the user typed; the coords (home_lat/home_lng) come back
  // from /me after geocoding completes server-side. We display the coords as
  // confirmation but don't let the user edit them directly.
  const [homeRegionLabel, setHomeRegionLabel] = useState(user?.home_region_label || '');
  const [appWideRadiusMi, setAppWideRadiusMi] = useState(user?.app_wide_radius_mi ?? 50);
  // Inline feedback for the region field after a save: an error string when
  // geocoding failed, or the resolved place name when it succeeded (so the
  // user can sanity-check a typo didn't match the wrong place).
  const [geocodeError, setGeocodeError] = useState(null);
  const [geocodeResolved, setGeocodeResolved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Push state (v85b)
  const [pushStatus, setPushStatus] = useState(null); // { supported, permission, subscribed }
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState('');

  // Reminder preferences
  const [reminderPrefs, setReminderPrefs] = useState({
    push_enabled: true,
    email_enabled: true,
    windows: { '24h': true, '2h': true, '30m': false },
  });
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderSaved, setReminderSaved] = useState(false);

  useEffect(() => {
    api.getReminderPrefs().then(prefs => {
      setReminderPrefs({
        push_enabled:  prefs.push_enabled,
        email_enabled: prefs.email_enabled,
        windows:       prefs.windows,
      });
    }).catch(() => {}); // fails gracefully if not logged in yet
  }, []);

  // When the user object updates (e.g. after geocoding completes server-side
  // and updateUser feeds the new home_lat/home_lng back), reflect that locally.
  useEffect(() => {
    if (user?.home_region_label !== undefined) {
      setHomeRegionLabel(user.home_region_label || '');
    }
    if (user?.app_wide_radius_mi != null) {
      setAppWideRadiusMi(user.app_wide_radius_mi);
    }
  }, [user?.home_region_label, user?.app_wide_radius_mi]);

  // Load push subscription status once on mount (v85b).
  useEffect(() => {
    (async () => {
      try { setPushStatus(await getSubscriptionStatus()); }
      catch { setPushStatus({ supported: false }); }
    })();
  }, []);

  const handleEnablePush = async () => {
    setPushBusy(true); setPushMessage('');
    const result = await requestAndSubscribe();
    if (result.ok) {
      setPushMessage('✓ Notifications enabled');
      setPushStatus(await getSubscriptionStatus());
    } else {
      setPushMessage(result.reason || 'Could not enable.');
    }
    setPushBusy(false);
  };

  const handleTestPush = async () => {
    setPushBusy(true); setPushMessage('');
    const result = await testPush();
    if (!result.ok) {
      setPushMessage(`Test failed: ${result.reason}`);
    } else if (result.devices === 0) {
      setPushMessage('No devices registered yet — tap "Enable push" first.');
    } else if (result.sent > 0) {
      setPushMessage(`✓ Sent to ${result.sent} device${result.sent === 1 ? '' : 's'} — check for the notification.`);
    } else if (result.dead > 0) {
      setPushMessage(`Subscription was expired — cleaned up ${result.dead}. Tap "Enable push" again.`);
      setPushStatus(await getSubscriptionStatus());
    } else if (result.skipped > 0) {
      setPushMessage('Push not configured on the server.');
    } else {
      setPushMessage(`Test failed for ${result.failed || 0} device(s).`);
    }
    setPushBusy(false);
  };

  const handleForceResubscribe = async () => {
    setPushBusy(true); setPushMessage('');
    const result = await forceResubscribe();
    if (result.ok) {
      setPushMessage('✓ Re-registered! Tap "Test push" to verify.');
      setPushStatus(await getSubscriptionStatus());
    } else {
      setPushMessage(result.reason || 'Re-registration failed.');
    }
    setPushBusy(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const regionChanged = homeRegionLabel !== (user?.home_region_label || '');
    try {
      const updated = await api.updateMe({
        phone,
        emailNotifications: emailNotif,
        emailPrefs,
        handicap: handicap !== '' ? parseFloat(handicap) : null,
        ghinNumber: ghinNumber || null,
        sharePhone,
        shareEmail,
        // Only send homeRegionLabel if it changed — sending an unchanged value
        // would still trigger an unnecessary geocode round-trip.
        ...(regionChanged ? { homeRegionLabel: homeRegionLabel || '' } : {}),
        appWideRadiusMi: parseInt(appWideRadiusMi),
      });
      updateUser(updated);
      // Surface geocode feedback only when we actually attempted a region update.
      if (regionChanged) {
        if (updated.geocode_failed) {
          setGeocodeError(`We couldn't find "${homeRegionLabel}". Check the spelling, or try a US zip code.`);
          setGeocodeResolved(null);
        } else if (updated.geocode_resolved) {
          setGeocodeError(null);
          setGeocodeResolved(updated.geocode_resolved);
        } else {
          // Region was cleared
          setGeocodeError(null);
          setGeocodeResolved(null);
        }
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handleReminderSave = async () => {
    setReminderSaving(true);
    try {
      await api.updateReminderPrefs(reminderPrefs);
      setReminderSaved(true);
      setTimeout(() => setReminderSaved(false), 2000);
    } catch (err) { alert(err.message); }
    finally { setReminderSaving(false); }
  };

  const toggleWindow = (key) => {
    setReminderPrefs(p => ({ ...p, windows: { ...p.windows, [key]: !p.windows[key] } }));
  };

  const handleLogout = () => { logout(); navigate('/auth'); };

  return (
    <div style={{ maxWidth: 520 }}>
      <h2 style={{ fontSize: 22, marginBottom: '0.9rem' }}>Profile &amp; settings</h2>

      {/* Identity card */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:'0.75rem' }}>
          <div style={{ width:52, height:52, borderRadius:'50%', background:'#2e7d4f',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:20, fontWeight:500, color:'#fff' }}>
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div>
            <div style={{ fontFamily:"'Playfair Display', serif", fontSize:19, color:'#0d2b1a' }}>
              {user?.first_name} {user?.last_name}
            </div>
            <div style={{ fontSize:12, color:'#7a7a6a', marginTop:1 }}>{user?.email}</div>
          </div>
        </div>
        {/* Share email toggle — parallel to the mobile-number "Share with
            friends" option. When on, your email shows on your card to people
            in your network so they can reach you directly. */}
        <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer',
                        fontSize:12, color:'#4a4a3a', marginTop:2 }}>
          <input type="checkbox" checked={shareEmail}
                 onChange={e => setShareEmail(e.target.checked)} style={{ width:'auto' }} />
          Share my email with friends
          <span style={{ fontSize:11, color:'#9a9a8a' }}>
            (visible to your golf network)
          </span>
        </label>
        <div style={{ marginTop:'0.7rem', display:'flex', gap:8, alignItems:'center' }}>
          <button className="btn btn-accent btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {saved && <span style={{ fontSize:12, color:'#2e7d4f' }}>✓ Saved</span>}
        </div>
      </div>

      {/* Contact + notifications */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ fontWeight:500, fontSize:14, color:'#0d2b1a', marginBottom:'0.85rem' }}>
          Notifications
        </div>
        <div style={{ background:'#e8f5ec', borderRadius:8, padding:'0.75rem', marginBottom:'0.85rem',
                      fontSize:13, color:'#1a5c32', lineHeight:1.6 }}>
          <strong>Push and email</strong> share the same per-event preferences below.
          When you turn off "Someone joins your tee time," both channels go quiet for that event.<br/>
          <span style={{ fontSize:11, color:'#2e7d4f' }}>
            Push requires installing Greentime to your home screen first (iPhone) and granting notification permission.
          </span>
        </div>
        <div className="form-group">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 4 }}>
            <label className="form-label" style={{ margin: 0 }}>MOBILE NUMBER</label>
            <label style={{ display:'flex', alignItems:'center', gap: 5, cursor:'pointer', fontSize: 11, color:'#4a4a3a' }}>
              <input type="checkbox" checked={sharePhone}
                     onChange={e => setSharePhone(e.target.checked)} style={{ width:'auto' }} />
              Share with friends
            </label>
          </div>
          <input className="form-input" type="tel" value={phone}
                 onChange={e => setPhone(e.target.value)} placeholder="(509) 555-0100" />
        </div>
        <div className="form-group">
          <label className="form-label">GHIN Number (optional)</label>
          <input className="form-input" type="text" value={ghinNumber}
                 onChange={e => setGhinNumber(e.target.value)} placeholder="e.g. 2108570" />
          <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 3 }}>
            Your USGA Golf Handicap ID. Used to display your handicap to other players.
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Handicap index (optional)</label>
          <input className="form-input" type="number" value={handicap} min="0" max="54" step="0.1"
                 onChange={e => setHandicap(e.target.value)} placeholder="e.g. 12.4" />
          <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 3 }}>
            Shown on player chips so your group knows who they're playing with.
          </div>
        </div>
        <div style={{ borderTop: '1px solid #e8e4d8', paddingTop: '0.85rem', marginTop: '0.5rem' }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#0d2b1a', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Communication preferences
          </div>

          {/* Master switch — when off, all category rows below are muted. */}
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer',
                          fontSize:13, color:'#4a4a3a', marginBottom: 10 }}>
            <input type="checkbox" checked={emailNotif}
                   onChange={e => setEmailNotif(e.target.checked)} style={{ width:'auto' }} />
            <strong style={{ color: '#0d2b1a' }}>Notifications</strong>
            <span style={{ fontSize: 11, color: '#7a7a6a' }}>(master switch — silences email and push)</span>
          </label>

          {/* v85: one toggle per event, gates BOTH email and push through the
              shared shouldSend() helper on the backend. */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 80px', alignItems:'center',
                        padding:'0 0 6px', borderBottom:'1px solid #e8e4d8',
                        opacity: emailNotif ? 1 : 0.45, transition:'opacity 0.15s' }}>
            <span />
            <span style={{ fontSize:10, fontWeight:500, color:'#4a4a3a', textAlign:'center',
                           textTransform:'uppercase', letterSpacing:'.04em' }}>Notify me</span>
          </div>

          <div style={{ opacity: emailNotif ? 1 : 0.45, pointerEvents: emailNotif ? 'auto' : 'none',
                        transition:'opacity 0.15s' }}>
            {EMAIL_PREF_GROUPS.map(group => (
              <div key={group.title}>
                <div style={{ fontSize:11, fontWeight:500, color:'#7a7a6a', letterSpacing:'.03em',
                              padding:'12px 0 2px' }}>
                  {group.title}
                </div>
                {group.rows.map(({ key, label }) => (
                  <div key={key} style={{ display:'grid', gridTemplateColumns:'1fr 80px',
                                          alignItems:'center', padding:'7px 0',
                                          borderTop:'1px solid #f0ede4' }}>
                    <span style={{ fontSize:12, color:'#4a4a3a', lineHeight:1.4, paddingRight:8 }}>
                      {label}
                    </span>
                    <div style={{ textAlign:'center' }}>
                      <input
                        type="checkbox"
                        checked={emailPrefs[key] !== false}
                        onChange={(e) => setEmailPrefs(p => ({ ...p, [key]: e.target.checked }))}
                        style={{ width:'auto' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {emailNotif && (
            <div style={{ fontSize:11, color:'#9a9a8a', marginTop:10, lineHeight:1.5 }}>
              Each toggle controls both email and push for that event.
              Reminder timing has its own settings below.
            </div>
          )}
        </div>

        <div style={{ marginTop:'1rem', display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <button className="btn btn-accent btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {saved && <span style={{ fontSize:12, color:'#2e7d4f' }}>✓ Saved</span>}

          {/* v85b push controls. Show Enable when not subscribed; Test when subscribed. */}
          {pushStatus?.supported && pushStatus?.subscribed && (
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              <button
                type="button"
                onClick={handleTestPush}
                disabled={pushBusy}
                className="btn btn-outline btn-sm"
                title="Sends a test push notification to all your registered devices."
              >
                {pushBusy ? 'Testing…' : 'Test push'}
              </button>
              <button
                type="button"
                onClick={handleForceResubscribe}
                disabled={pushBusy}
                className="btn btn-outline btn-sm"
                title="Re-registers this device for push notifications. Use if Test push says no devices."
              >
                {pushBusy ? 'Working…' : 'Re-register device'}
              </button>
            </div>
          )}
          {pushStatus?.supported && !pushStatus?.subscribed && pushStatus?.permission !== 'denied' && (
            <button
              type="button"
              onClick={handleEnablePush}
              disabled={pushBusy}
              className="btn btn-outline btn-sm"
            >
              {pushBusy ? 'Enabling…' : 'Enable push on this device'}
            </button>
          )}
        </div>
        {pushMessage && (
          <div style={{ fontSize:12, color: pushMessage.startsWith('✓') ? '#2e7d4f' : '#9a4a2a', marginTop:6 }}>
            {pushMessage}
          </div>
        )}
        {pushStatus?.supported && pushStatus?.permission === 'denied' && (
          <div style={{ fontSize:11, color:'#9a4a2a', marginTop:6, lineHeight:1.5 }}>
            Notifications are blocked for this site. Enable them in your browser/iOS settings,
            then return here and tap "Enable push on this device."
          </div>
        )}
      </div>

      {/* App-Wide discovery: home region + radius. */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ fontWeight:500, fontSize:14, color:'#0d2b1a', marginBottom:'0.5rem' }}>
          📡 App-Wide discovery
        </div>
        <div style={{ fontSize:12, color:'#7a7a6a', marginBottom:'0.85rem', lineHeight:1.5 }}>
          Set a home region so Greentime can show App-Wide tee times near you.
          You'll see open tee times within your radius from any Greentime user
          who shares them App-Wide. No location data is shared with other users —
          only with the server, to compute distance.
        </div>

        <div className="form-group">
          <label className="form-label">Home region</label>
          <input
            className="form-input"
            type="text"
            value={homeRegionLabel}
            onChange={e => { setHomeRegionLabel(e.target.value); setGeocodeError(null); setGeocodeResolved(null); }}
            placeholder="ZIP code (e.g. 99201) or city, state"
          />
          <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 3, lineHeight: 1.5 }}>
            {geocodeError ? (
              // Save just failed to locate what they typed — loud inline error.
              <span style={{ color: '#c0392b' }}>{geocodeError}</span>
            ) : geocodeResolved ? (
              // Save just succeeded — echo what it matched so a wrong match
              // (e.g. a typo landing on the wrong town) is immediately visible.
              <span style={{ color: '#2e7d4f' }}>
                ✓ Matched: {geocodeResolved}
              </span>
            ) : user?.home_lat && user?.home_lng ? (
              <>✓ Located at {parseFloat(user.home_lat).toFixed(3)}, {parseFloat(user.home_lng).toFixed(3)}
                {user.location_source === 'played_centroid' && (
                  <span style={{ color: '#9a8a5a', marginLeft: 4 }}>
                    (auto-detected from courses you've played — enter a region to override)
                  </span>
                )}
              </>
            ) : homeRegionLabel ? (
              <span style={{ color: '#a66a0a' }}>
                Not located yet — save to look this up. A US ZIP code works best.
              </span>
            ) : (
              <>Enter a ZIP code or city/state. Leave blank to use played-with courses as your anchor (set automatically after joining a few tee times).</>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Discovery radius</label>
          <select
            className="form-input"
            value={appWideRadiusMi}
            onChange={e => setAppWideRadiusMi(parseInt(e.target.value))}
          >
            <option value={25}>25 miles</option>
            <option value={50}>50 miles (default)</option>
            <option value={100}>100 miles</option>
            <option value={200}>200 miles</option>
            <option value={500}>500 miles</option>
          </select>
          <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 3 }}>
            Tee times posted as App-Wide show up if the course is within this distance.
          </div>
        </div>

        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button className="btn btn-accent btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save region'}
          </button>
          {saved && <span style={{ fontSize:12, color:'#2e7d4f' }}>✓ Saved</span>}
        </div>
      </div>

      {/* Reminder preferences */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ fontWeight:500, fontSize:14, color:'#0d2b1a', marginBottom:'0.5rem' }}>
          Tee time reminders
        </div>
        <div style={{ fontSize:12, color:'#7a7a6a', marginBottom:'0.85rem', lineHeight:1.5 }}>
          Get a push notification before each tee time you're in.
          Choose how far in advance — you can pick multiple.
        </div>

        {/* Channel toggles */}
        <div style={{ display:'flex', gap:16, marginBottom:'0.85rem', flexWrap:'wrap' }}>
          <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontSize:13, color:'#4a4a3a' }}>
            <input type="checkbox" checked={reminderPrefs.push_enabled}
                   onChange={e => setReminderPrefs(p => ({ ...p, push_enabled: e.target.checked }))}
                   style={{ width:'auto' }} />
            Push notification
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontSize:13, color:'#4a4a3a' }}>
            <input type="checkbox" checked={reminderPrefs.email_enabled}
                   onChange={e => setReminderPrefs(p => ({ ...p, email_enabled: e.target.checked }))}
                   style={{ width:'auto' }} />
            Email (24 hr only)
          </label>
        </div>

        {/* Window checkboxes */}
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {Object.entries(WINDOW_LABELS).map(([key, label]) => (
            <label key={key} style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer',
                                      background: reminderPrefs.windows[key] ? '#e8f5ec' : '#f9f7f3',
                                      borderRadius:8, padding:'8px 12px',
                                      border:`1.5px solid ${reminderPrefs.windows[key] ? '#7ec99a' : '#e0dcd0'}`,
                                      transition:'all 0.15s' }}>
              <input type="checkbox" checked={!!reminderPrefs.windows[key]}
                     onChange={() => toggleWindow(key)} style={{ width:'auto' }} />
              <div>
                <div style={{ fontSize:13, fontWeight:500, color:'#0d2b1a' }}>{label}</div>
                <div style={{ fontSize:11, color:'#7a7a6a' }}>
                  {key === '24h' && 'Good for planning travel, gear, or carpools'}
                  {key === '2h'  && 'Last chance to review the group thread'}
                  {key === '30m' && 'Time to leave — heads up alert'}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div style={{ marginTop:'1rem', display:'flex', gap:8, alignItems:'center' }}>
          <button className="btn btn-accent btn-sm" onClick={handleReminderSave} disabled={reminderSaving}>
            {reminderSaving ? 'Saving…' : 'Save reminders'}
          </button>
          {reminderSaved && <span style={{ fontSize:12, color:'#2e7d4f' }}>✓ Saved</span>}
        </div>
      </div>

      {/* Buy me a latte */}
      <BuyMeALatte />

      {/* Account */}
      <div className="card">
        <div style={{ fontWeight:500, fontSize:14, color:'#0d2b1a', marginBottom:'0.75rem' }}>Account</div>
        <p style={{ fontSize:12, color:'#7a7a6a', marginBottom:'0.75rem' }}>
          Member since {new Date(user?.created_at).toLocaleDateString('en-US', { month:'long', year:'numeric' })}
        </p>
        <button className="btn btn-outline btn-sm" onClick={handleLogout}>Sign out</button>
      </div>
    </div>
  );
}
